import { Controller } from '@hotwired/stimulus'

// Stepper de titulares (1-3) + tabs en la Pantalla 1 de subida de
// documentación de hipotecas (ENH-RNK-025 A-15, prototipo Naiara).
//
// Estado:
//   - numTitulares: 1 (default), 2 ó 3
//   - currentTab: número del titular cuyo set de inputs/dropzones está
//     visible. Por defecto 1.
//
// Markup esperado:
//   - El HAML pinta TODOS los bloques de titular (T1, T2, T3) en el DOM
//     dentro de cada tipo de documento. Cada bloque lleva un atributo
//     data-titular="N". Al cambiar de tab se muestra solo el del titular
//     activo (los demás quedan con [hidden]).
//   - El bloque LOPD (`[data-lopd-block]`) es global por solicitud y se
//     mantiene visible en todos los titulares (iteración prototipo
//     paelladoc 2026-05-13, PR Soluciones/paelladoc#1822).
//
// Targets:
//   - contador: span donde se renderiza el número de titulares
//   - unidad: span con "titular" / "titulares"
//   - btnDecrementar / btnIncrementar: botones del stepper
//   - tabsWrap: contenedor del bloque de tabs (oculto si numTitulares === 1)
//   - tabsContainer: contenedor donde se renderizan dinámicamente los tabs
//
// Eventos:
//   - Emite `hipotecas-doc:tab-changed` cuando cambia currentTab
//   - Emite `hipotecas-doc:titulares-changed` cuando cambia numTitulares
export default class extends Controller {
  static targets = [
    'contador',
    'unidad',
    'btnDecrementar',
    'btnIncrementar',
    'tabsWrap',
    'tabsContainer',
    'numTitularesField',
    'avisoBloqueo'
  ]

  static values = {
    min: { type: Number, default: 1 },
    max: { type: Number, default: 3 },
    // Titulares que ya constan (el cotitular se guarda al abrir la firma online
    // con dos): quien vuelve los encuentra declarados.
    inicial: { type: Number, default: 1 },
    // Ya firmó la LOPD online: los titulares quedan fijados. Como único titular,
    // el documento no lleva cotitular y no se puede añadir (producto,
    // 5-oct-2026); con los dos, no se puede quitar al cotitular.
    bloqueado: { type: Boolean, default: false },
    tiposObligatorios: { type: Array, default: ['dni', 'nomina', 'irpf', 'vida_laboral'] }
  }

  connect () {
    this.numTitulares = Math.min(Math.max(this.inicialValue, this.minValue), this.maxValue)
    this.bloqueado = this.bloqueadoValue
    this.currentTab = 1
    this.actualizarStepper()
    this.renderTabs()
    this.aplicarTitularActivo()
    // Refrescar el estado de las tabs (gris/naranja/verde + contador) cada
    // vez que el usuario sube/elimina un archivo en cualquier dropzone.
    this.handleFileChangeBound = this.handleFileChange.bind(this)
    this.element.addEventListener('change', this.handleFileChangeBound)
  }

  disconnect () {
    if (this.handleFileChangeBound) this.element.removeEventListener('change', this.handleFileChangeBound)
  }

  handleFileChange (event) {
    if (event.target.matches && event.target.matches('input[type="file"]')) {
      this.actualizarEstadoTabs()
    }
  }

  incrementar () {
    if (this.bloqueado || this.numTitulares >= this.maxValue) return
    this.numTitulares += 1
    this.actualizarStepper()
    this.renderTabs()
    this.notificarTitulares()
  }

  decrementar () {
    if (this.bloqueado || this.numTitulares <= this.minValue) return
    // Si el tab activo desaparece, vuelve al T1.
    if (this.currentTab >= this.numTitulares) this.cambiarA(1)
    // Limpiar inputs del titular que se elimina antes de cambiar el contador.
    this.limpiarInputsDelTitular(this.numTitulares)
    this.numTitulares -= 1
    this.actualizarStepper()
    this.renderTabs()
    this.aplicarTitularActivo()
    this.notificarTitulares()
  }

  // La firma online terminó (eventos del controller hipotecas-lopd-firma-online).
  // El documento firmado fija los titulares: con uno no lleva cotitular, y con
  // dos ya lo lleva (bajar a uno lo dejaba firmado con un solo titular).
  firmaHecha () {
    this.bloquear(true)
  }

  // La firma no llegó a confirmarse y vuelve a ofrecerse: se puede volver a
  // elegir. Un bloqueo que ya traía la página (firma registrada) se mantiene.
  firmaDeshecha () {
    if (!this.bloqueadoValue) this.bloquear(false)
  }

  cambiarA (n) {
    if (n < 1 || n > this.numTitulares) return
    this.currentTab = n
    this.aplicarTitularActivo()
    this.actualizarEstadoTabs()
    this.notificarTab()
  }

  // ---- privado ----

  actualizarStepper () {
    if (this.hasContadorTarget) this.contadorTarget.textContent = this.numTitulares
    if (this.hasUnidadTarget) {
      this.unidadTarget.textContent = this.numTitulares === 1
        ? this.unidadSingular()
        : this.unidadPlural()
    }
    if (this.hasBtnDecrementarTarget) this.btnDecrementarTarget.disabled = this.bloqueado || this.numTitulares <= this.minValue
    if (this.hasBtnIncrementarTarget) this.btnIncrementarTarget.disabled = this.bloqueado || this.numTitulares >= this.maxValue
    // El aviso («has firmado como único titular, llámanos») solo aplica a uno.
    if (this.hasAvisoBloqueoTarget) this.avisoBloqueoTarget.hidden = !(this.bloqueado && this.numTitulares === 1)
    // El hidden field viaja con el form para que el backend pueda exigir
    // documentación completa por titular (A-20 / Naiara 2026-05-08 10:52).
    if (this.hasNumTitularesFieldTarget) this.numTitularesFieldTarget.value = this.numTitulares
  }

  bloquear (bloqueado) {
    this.bloqueado = bloqueado
    this.actualizarStepper()
  }

  unidadSingular () { return this.data.get('unidadSingular') || 'titular' }
  unidadPlural () { return this.data.get('unidadPlural') || 'titulares' }
  tabLabel (n) { return (this.data.get('tabLabel') || 'Titular %{n}').replace('%{n}', n) }

  renderTabs () {
    if (!this.hasTabsContainerTarget) return
    if (this.numTitulares <= 1) {
      if (this.hasTabsWrapTarget) this.tabsWrapTarget.hidden = true
      this.tabsContainerTarget.innerHTML = ''
      return
    }
    if (this.hasTabsWrapTarget) this.tabsWrapTarget.hidden = false
    this.tabsContainerTarget.innerHTML = ''
    for (let i = 1; i <= this.numTitulares; i += 1) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'rnk-HipotecasDoc_TitTab'
      btn.dataset.action = 'hipotecas-doc-titulares#tabClick'
      btn.dataset.titular = String(i)
      // El span del icono va vacío: lo pinta la máscara CSS de
      // .rnk-HipotecasDoc_TitTabIcon con el icono Lucide `user`. Antes era el
      // emoji de persona, que desentonaba con el resto de la iconografía
      // (ENH-RNK-1855, ajuste de Naiara 28-jul-2026).
      btn.innerHTML = `<span class="rnk-HipotecasDoc_TitTabIcon" aria-hidden="true"></span> ${this.tabLabel(i)} <span class="rnk-HipotecasDoc_TitTabDot" data-titular-dot="${i}">0</span>`
      this.tabsContainerTarget.appendChild(btn)
    }
    this.actualizarEstadoTabs()
  }

  tabClick (event) {
    const n = parseInt(event.currentTarget.dataset.titular, 10)
    this.cambiarA(n)
  }

  // Calcula cuántos obligatorios tiene cubiertos cada titular y aplica
  // las clases visuales en cada tab:
  //   - activo (azul)       → siempre, independientemente del progreso
  //   - complete (verde)    → 4/4 obligatorios subidos
  //   - partial (naranja)   → 1..3 obligatorios subidos
  //   - default (gris)      → 0 obligatorios
  // El dot dentro de cada tab muestra el count del titular correspondiente.
  actualizarEstadoTabs () {
    if (!this.hasTabsContainerTarget) return
    const tipos = this.tiposObligatoriosValue
    const total = tipos.length
    this.tabsContainerTarget.querySelectorAll('.rnk-HipotecasDoc_TitTab').forEach(tab => {
      const n = parseInt(tab.dataset.titular, 10)
      const filled = this.contarObligatoriosLlenos(n, tipos)
      tab.classList.remove('rnk-HipotecasDoc_TitTab-active', 'rnk-HipotecasDoc_TitTab-partial', 'rnk-HipotecasDoc_TitTab-complete')
      if (n === this.currentTab) {
        tab.classList.add('rnk-HipotecasDoc_TitTab-active')
      } else if (filled === total) {
        tab.classList.add('rnk-HipotecasDoc_TitTab-complete')
      } else if (filled > 0) {
        tab.classList.add('rnk-HipotecasDoc_TitTab-partial')
      }
      const dot = tab.querySelector('.rnk-HipotecasDoc_TitTabDot')
      if (dot) dot.textContent = String(filled)
    })
  }

  contarObligatoriosLlenos (titularN, tipos) {
    let count = 0
    tipos.forEach(tipo => {
      const item = this.element.querySelector(`.rnk-HipotecasDoc_AccItem[data-tipo="${tipo}"]`)
      if (!item) return
      const input = item.querySelector(`[data-titular="${titularN}"] input[type="file"]`)
      if (input && input.files && input.files.length > 0) count += 1
    })
    return count
  }

  aplicarTitularActivo () {
    const root = this.element
    root.querySelectorAll('[data-titular]').forEach(el => {
      const n = parseInt(el.dataset.titular, 10)
      el.hidden = n !== this.currentTab
    })
  }

  // Borra TODA la documentación subida en los bloques de un titular (los
  // dropzones por tipo que viven dentro de `[data-titular="n"]`). Cada
  // dropzone tiene su propio buffer interno `this.archivos`, así que
  // delegamos en `dropzone#reset()` para vaciar buffer + input + UI a la
  // vez. Si solo limpiásemos `input.value = ''` el buffer quedaba sucio
  // y los archivos reaparecían al volver a añadir el titular.
  limpiarInputsDelTitular (n) {
    this.element.querySelectorAll(`[data-titular="${n}"] [data-controller~="hipotecas-doc-dropzone"]`).forEach(dropzoneEl => {
      const dropzone = this.application.getControllerForElementAndIdentifier(
        dropzoneEl,
        'hipotecas-doc-dropzone'
      )
      if (dropzone && typeof dropzone.reset === 'function') dropzone.reset()
    })
  }

  notificarTab () {
    this.dispatch('tab-changed', { detail: { titular: this.currentTab } })
  }

  notificarTitulares () {
    this.dispatch('titulares-changed', { detail: { num: this.numTitulares } })
  }
}
